import { Metric } from './Metric';
import { send } from '../metricsApi';

/**
 * `Histogram`s track percentile buckets based on the reported values.
 * A `Histogram` reports a collection of values, which a consumer can use to
 * either report on individually, or aggregate into summary metrics (e.g. p99).
 * Common histograms might be the number of objects a user is loading on a
 * page, or the number of times that a component re-renders.
 *
 * This class should never be directly constructed, and `flush` should never be
 * called outside of the Daemon. Metric lifecycles should be fully managed by
 * a `Metrics` factory, Managing construction via a central factory allows us
 * to cache metric instances and avoid reporting conflicting values for the
 * same metric in the same reporting period.
 *
 * @example
 * const histogram = new Histogram('my-metric', {some_dimention: 'val'})
 * histogram.update(9);
 * histogram.flush(); // { 'my-metric': [9] }
 */
class Histogram extends Metric {
  constructor(...args) {
    super(...args);
    this.values = [];
  }
  update(value) {
    if (typeof value !== 'number' || !isFinite(value)) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(`[metrics-js] Dropping non-numeric value ${value} for histogram ${this.getName()}`);
      }
      return;
    }
    if (value < 0) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(`[metrics-js] Dropping negative value ${value} for histogram ${this.getName()}`);
      }
      return;
    }
    if (this.values.length >= 10000) {
      const message = `[metrics-js] Histogram ${this.getName()} has exceeded the maximum of 10,000 observations per reporting period. Flushing current batch and starting a new one.`;
      if (process.env.NODE_ENV !== 'production') {
        console.log(message);
      }
      send([{
        name: this.getName(),
        type: 'HISTOGRAM',
        values: this.values,
        dimensions: this.getDimensions()
      }]);
      this.values = [];
    }
    this.values.push(value);
  }
  canFlush() {
    return this.values.length > 0;
  }
  flush() {
    const values = this.values;
    this.values = [];
    return {
      name: this.getName(),
      type: 'HISTOGRAM',
      values,
      dimensions: this.getDimensions()
    };
  }
}
export { Histogram };